import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Chip, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { fieldTypes } from '@/helpers/constants';
import { formatDate, getNativeDateValue } from '@/helpers/utils';
import Dropdown from '../Dropdown/Dropdown';
import InputText from '../InputText/InputText';
import MandatoryLabel from '../MandatoryLabel/MandatoryLabel';
import HighlightMask from '../../HighlightMask/HighlightMask';
import styles from './VehicleCleanDirtInput.module.scss';

const useStyles = makeStyles(() => ({
  fourColumns: { width: '25%', margin: '0 1% 0 0' },
  threeColumns: { width: '32%', margin: '0 1% 10px 0', '&:nth-child(3n)': { marginRight: 0 } },
  twoColumns: { width: '50%', margin: '0 1% 0 0' },
  oneColumns: { width: '100%', margin: '0 1% 0 0' },
  columComment: { flexBasis: '60%' },
  calendar: { marginLeft: '2px', width: '100%' },
  input: { width: '99%' },
  chip: { borderRadius: 4, width: '100%', justifyContent: 'space-between' },
}));

const VehicleCleanDirtInput = (props) => {
  const { checkEnabled, chipProps, calendarProps, inputProps, inputPropsTime, drowpdownProps, additionalInputProps, groupId,
    optionKey } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const calendarLabel = (<>{calendarProps?.label} <MandatoryLabel /></>);

  const checkDate = (value) => {
    if (!value) {
      return calendarProps?.onChange(calendarProps?.section, calendarProps?.code, calendarProps?.index, '');
    }

    if (value && value.toString() !== 'Invalid Date') {
      return calendarProps?.onChange(calendarProps?.section, calendarProps?.code, calendarProps?.index, formatDate(value));
    }

    return undefined;
  };

  const handleOnBlur = (ev) => {
    const translatableFields = ['Date', 'Other description'];

    if (translatableFields.includes(inputProps.code) || additionalInputProps?.id === 'VehicleCleanDirt-additionalInfo') {
      inputProps.detectLanguage(ev.target.value);
    }
  };

  const handleChangeValue = (ev) => {
    inputProps.onChange(inputProps?.section, inputProps?.code, inputProps?.index, ev.target.value);
  };

  const getMainFieldPath = (section) => {
    const path = ['limitationVolumeManualEntry', `vehicleGroups[${groupId}]`];

    if (section === 'dirtPoint') {
      path.push('dirtPointCriteria');
    } else {
      path.push('cleanPointCriteria');
    }

    return path;
  };

  const getInputFieldPath = (propsInput) => {
    const path = getMainFieldPath(propsInput?.section);

    path.push(`${optionKey}[${propsInput?.index}]`);

    if (propsInput?.key.includes('dates') && !propsInput?.key.includes('vehicleIdentificationNumbers')) {
      path.push('textDate');
    }

    if (optionKey === 'releases') {
      path.push(propsInput?.id?.replace('VehicleCleanDirt-', ''));
    }

    return path;
  };

  const getCalendarInputFieldPath = () => {
    const path = getMainFieldPath(calendarProps?.section);

    if (optionKey === 'ttzs') {
      path.push(`ttzs[${calendarProps?.index}]`);
    }

    if (inputProps?.key.includes('dates')) {
      path.push(`dates[${calendarProps?.index}]`);
      path.push('calendarDate');
    }

    return path;
  };

  return (
    <div className={styles.InputsContainer}>
      {chipProps?.show && (
        <div className={styles.Chip}>
          <Chip
            disabled={checkEnabled}
            key={chipProps?.key}
            clickable
            label={chipProps?.label}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            className={classes.chip}
            onDelete={() => chipProps?.onDelete(chipProps?.section, chipProps?.code, chipProps?.index)}
          />
        </div>
      )}

      {calendarProps?.show && (
        <div className={styles.Calendar}>
          <FormControlLabel
            disabled={checkEnabled}
            className={classes.calendar}
            control={(
              <HighlightMask
                fieldId={calendarProps?.key}
                fieldPath={getCalendarInputFieldPath()}
                value={calendarProps?.value}
                fieldType={fieldTypes.CALENDAR}
                disabled={checkEnabled}
                noTrasnslationField
              >
                <div>
                  <TextField
                    disabled={checkEnabled}
                    key={calendarProps?.key}
                    id={calendarProps?.key}
                    label={calendarLabel}
                    type="date"
                    value={getNativeDateValue(calendarProps?.value)}
                    onChange={(event) => {
                      const newValue = event.target.value ? new Date(`${event.target.value}T00:00:00`) : null;
                      checkDate(newValue);
                    }}
                    error={calendarProps?.invalid}
                    variant="outlined"
                    placeholder=""
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      className: calendarProps?.invalid ? 'validation-warning' : '',
                      autoComplete: 'off',
                    }}
                    fullWidth
                    className={classes.oneColumns}
                  />
                </div>
              </HighlightMask>
            )}
          />
        </div>
      )}
      {inputPropsTime?.show && (
        <div className={styles.Input}>
          <InputText
            isMandatory
            invalid={inputPropsTime?.invalid}
            className={classes.input}
            disabled={checkEnabled}
            maxLength={40}
            key={inputPropsTime?.key}
            id={inputPropsTime?.key}
            type={inputPropsTime?.type}
            label={inputPropsTime?.label}
            handleChange={(ev) => inputPropsTime?.onChange(inputProps?.section, inputPropsTime?.code, inputProps?.index, ev.target.value, true)}
            value={inputPropsTime?.value || ''}
            fieldPath={['affectedVehicles', 'limitationVolumeManualEntry', `vehicleGroups[${groupId}]`, `${inputPropsTime.section === 'VehicleCleanDirt' ? 'dirtPointCriteria' : 'cleanPointCriteria'} `, `${optionKey} [${calendarProps?.index}].calendarDate`]}
            noTrasnslationField
          />
        </div>

      )}
      {inputProps?.show && (
        <div className={`${styles.Input} ${inputProps?.fullWidth ? styles.FullInput : ''} `}>
          <InputText
            isMandatory={inputProps?.isOptional !== true}
            invalid={inputProps?.invalid}
            className={classes.input}
            disabled={checkEnabled}
            maxLength={inputProps?.charLimit}
            key={inputProps?.key}
            id={inputProps?.key}
            label={inputProps?.label}
            handleChange={handleChangeValue}
            value={inputProps?.value || ''}
            onBlur={handleOnBlur}
            fieldPath={getInputFieldPath(inputProps)}
            multiline
            noTrasnslationField={optionKey !== 'otherDescriptions' || additionalInputProps?.id !== 'additionalInfo'
              || !inputProps.key.includes('textDate')}
            helperText={(inputProps?.value?.length >= inputProps?.charLimit)
              ? [t('validations.maxLength', { maxLength: inputProps?.charLimit })]
              : []}
          />
        </div>
      )}

      {additionalInputProps?.show && (
        <div className={`${styles.Input} ${additionalInputProps?.fullWidth ? styles.FullInput : ''} `}>
          <InputText
            isMandatory={additionalInputProps?.isOptional !== true}
            invalid={additionalInputProps?.invalid}
            className={classes.input}
            disabled={checkEnabled}
            maxLength={additionalInputProps?.charLimit}
            key={additionalInputProps?.key}
            id={additionalInputProps?.key}
            label={additionalInputProps?.label}
            handleChange={(ev) => additionalInputProps.onChange(inputProps?.section, inputProps?.code, inputProps?.index, ev.target.value, false, false, true)}
            value={additionalInputProps?.value || ''}
            onBlur={handleOnBlur}
            fieldPath={getInputFieldPath(additionalInputProps)}
            multiline
            noTrasnslationField={optionKey !== 'otherDescriptions' || additionalInputProps?.key !== 'additionalInfo'
              || !additionalInputProps.key.includes('textDate')}
            helperText={(additionalInputProps?.value?.length >= additionalInputProps?.charLimit)
              ? [t('validations.maxLength', { maxLength: additionalInputProps?.charLimit })]
              : []}
          />
        </div>
      )}

      {drowpdownProps?.show && (
        <div className={styles.FullInput}>
          <Dropdown
            id={drowpdownProps?.key}
            key={drowpdownProps?.key}
            label={<><MandatoryLabel />{drowpdownProps?.label}</>}
            disabled={checkEnabled}
            options={drowpdownProps?.form}
            value={drowpdownProps?.value || ''}
            invalid={drowpdownProps?.invalid}
            handleChange={(ev) => drowpdownProps?.onChange(inputProps?.section, inputPropsTime?.code, inputProps?.index, ev.target.value, false, true)}
            formControlClass={styles.DropdownSize}
            fieldPath={getInputFieldPath(drowpdownProps)}
            helperText={(drowpdownProps?.invalid) ? [t('validations.required')] : []}
          />
        </div>
      )}
    </div>
  );
};

export default VehicleCleanDirtInput;
